import { useState, lazy } from 'react';
import {
  TeBadge,
  TeButton,
  TeButtonMinimal,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeGrid,
  TeText,
  TeCallout,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlaceholderContainer, useNotification } from '@topcon/topcon-utils';

import { Section } from '../../utils/styling';
import { AppRoutes, BusinessPartnerType } from '../../app/routes/utils';
import { useCanAccess } from '../../app/routes/access';
import RenderDivisions from '../Divisions/RenderDivisions';
import UsersKPI from './UsersKPI';
import OverviewLoader from './OverviewLoader';
import { useAppService } from '../../providers/app/app.context';
import RenderApps from '../Apps/RenderApps';
import EmptyApps from '../Apps/EmptyApps';
import RequestToJoinInfoPopover from '../../components/Company/RequestToJoinInfoPopover';
import { useRequests } from '../UserManagement/Requests/requestsToJoin.queries';
import { useUsers, useInvitations } from '../UserManagement/userManagement.queries';
import { useOauthClients } from '../Apps/apps.queries';
import { useMigrationCandidateUsers } from '../../hooks/queries/migration.queries';
import { useCurrentBusinessPartnerWithIdentityInfo } from '../../hooks/queries/appSession.queries';
import { useAggregateDivisions } from '../../hooks/useAggregateDivisions';
import { useCriticalFontsReady } from '../../hooks/useCriticalFontsReady';
import { useCancelRequestToJoin } from '../UserManagement/Requests/requestsToJoin.mutations';
import { useCreateRequestToJoin } from '../Companies/companies.mutations';
import { BusinessPartnerAccessabilityEnum, RequestToJoinBody } from '../../api/generated/pap';
import { background } from '../../providers/theme/theme.index';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
// import ContentBlocks from '../../components/ContentBlock/ContentBlocks';
import Carousel from '../../components/Carousel/Carousel';
import CompanyIcon from '../../components/Company/CompanyIcon';
import { MigrationCallout } from '../../components/Callout/MigrationCallout';
import CustomerMigrationCallout from '../../components/ServiceAgreement/Customer/CustomerMigrationCallout';
import featureFlags from '../../utils/featureFlags';
const CustomersKPI = lazy(() => import('./CustomersKPI'));

function GenericOverview() {
  const showDivisions = useCanAccess(AppRoutes.Divisions);
  const [dismissedCallouts, setDismissedCallouts] = useState<Set<string>>(new Set());
  const {
    currentBusinessPartner,
    loadedBusinessPartner,
    identity,
    loadedIdentity,
    isOwner,
    isUserAdmin,
    isCustomerAdmin,
  } = useAppService();
  const {
    divisions: combinedDivisions,
    isFetched: divisionsIsFetched,
    refetch: refetchDivisions,
  } = useAggregateDivisions();
  const {
    data: currentBusinessPartnerWithIdentityInfo,
    isFetched: currentBusinessPartnerWithIdentityInfoIsFetched,
  } = useCurrentBusinessPartnerWithIdentityInfo();
  const { data: users, isFetched: usersIsFetched } = useUsers(isUserAdmin);
  const { data: invitations, isFetched: invitationsIsFetched } = useInvitations(isUserAdmin);
  const {
    data: requests,
    isFetched: requestsIsFetched,
    refetch: refetchRequests,
  } = useRequests(isUserAdmin);
  const { data: apps, isFetched: appsIsFetched } = useOauthClients();
  const isCriticalFontsReady = useCriticalFontsReady();
  const loaded =
    loadedBusinessPartner &&
    divisionsIsFetched &&
    currentBusinessPartnerWithIdentityInfoIsFetched &&
    loadedIdentity &&
    (usersIsFetched || !isUserAdmin) &&
    (invitationsIsFetched || !isUserAdmin) &&
    (requestsIsFetched || !isUserAdmin) &&
    appsIsFetched &&
    isCriticalFontsReady;

  const canRequestToJoin =
    currentBusinessPartnerWithIdentityInfo &&
    currentBusinessPartnerWithIdentityInfo.isSameDomain &&
    currentBusinessPartnerWithIdentityInfo.accessability ===
      BusinessPartnerAccessabilityEnum.REQUEST_TO_JOIN &&
    !currentBusinessPartnerWithIdentityInfo.user &&
    !currentBusinessPartnerWithIdentityInfo.isOwner;

  const { data: migrationCandidateUsers } = useMigrationCandidateUsers();

  const hasMigrationCandidates = featureFlags.MIGRATION && !!migrationCandidateUsers?.items?.length;

  const hasMigrationCandidatesUsers = hasMigrationCandidates && !dismissedCallouts.has('users');

  const joinRequest = requests?.find((request) => {
    return identity && identity.id === request.identityId;
  });

  const userProfileIds = currentBusinessPartnerWithIdentityInfo?.user?.profileIds || [];

  const { t } = useTranslation(['common', 'profile']);
  const navigate = useNavigate();
  const { mutateAsync: requestToJoin, isPending: requestPending } = useCreateRequestToJoin();
  const { mutateAsync: cancelRequestToJoin, isPending: cancelPending } = useCancelRequestToJoin();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  const addJoinRequest = async (businessPartnerId: string, name: string) => {
    try {
      await requestToJoin({
        businessPartnerId,
      } as RequestToJoinBody);
      refetchDivisions();
      refetchRequests(); // TODO: Remove this and use invalidateQueries in mutations
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('requestToJoinFailed', { ns: 'common' }),
          t('requestToJoinFailedDescription', { ns: 'common', name }),
          'error'
        );
      }
    }
  };

  const cancelRequest = async (id: string, name: string) => {
    try {
      await cancelRequestToJoin(id);
      refetchDivisions();
      refetchRequests();
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('requestCancelFailed', { ns: 'common' }),
          t('requestCancelFailedDescription', { ns: 'common', name }),
          'error'
        );
      }
    }
  };

  if (!loaded || !identity) return <OverviewLoader variant="organization" />;

  const visibleApps =
    apps?.items
      .filter(({ name }: { name: string }) => {
        if (name === '' || name === 'Devices' || name === 'Licenses') {
          return false;
        }
        return true;
      })
      .slice(0, 5) || [];

  const handleCalloutDismiss = (type: 'users' | 'requestToJoin') => {
    setDismissedCallouts((prev) => new Set(prev).add(type));
  };

  return (
    <TeContainer column grow padding="xs" gap="xs" overflow="auto" alignItems="center">
      <Section>
        <TeContainer padding="l" fill justifyContent="space-between" alignItems="center">
          <TeContainerSlim gap="m" alignItems="center">
            <CompanyIcon size="xl" iconURL={currentBusinessPartner?.iconURL} />
            <TeContainerSlim column gap="s">
              <TeText size={20} weight={700}>
                {currentBusinessPartner?.name}
              </TeText>
              <TeContainerSlim gap="xs">
                {isOwner && (
                  <TeBadge size="s" backgroundColor="background.inverse" text={t('owner')} />
                )}
                {userProfileIds.map((profileId: string) => {
                  return (
                    <TeBadge
                      key={profileId}
                      size="s"
                      backgroundColor="background.quaternary"
                      textColor="text.primary"
                      text={t(profileId, { ns: 'profile' })}
                    />
                  );
                })}
              </TeContainerSlim>
              {canRequestToJoin && (
                <TeText size={12} weight={400} color="text.secondary">
                  {joinRequest
                    ? t('requestedToJoin', { ns: 'common' })
                    : t('notJoined', { ns: 'common' })}
                </TeText>
              )}
            </TeContainerSlim>
          </TeContainerSlim>
          {canRequestToJoin && (
            <TeContainerSlim gap="xs" alignItems="center">
              {joinRequest && (
                <RequestToJoinInfoPopover
                  companyId={currentBusinessPartnerWithIdentityInfo!.id}
                  requestToJoinId={joinRequest?.id}
                />
              )}
              {joinRequest ? (
                <TeButton
                  variant="neutral"
                  size="s"
                  disabled={cancelPending}
                  text={t('cancelRequestToJoin', { ns: 'common' })}
                  teclick={() => {
                    cancelRequest(joinRequest.id, currentBusinessPartnerWithIdentityInfo.name);
                  }}
                />
              ) : (
                <TeButton
                  size="s"
                  disabled={requestPending}
                  text={t('requestToJoin', { ns: 'common' })}
                  teclick={() =>
                    addJoinRequest(
                      currentBusinessPartnerWithIdentityInfo.id,
                      currentBusinessPartnerWithIdentityInfo.name
                    )
                  }
                />
              )}
            </TeContainerSlim>
          )}
        </TeContainer>
      </Section>

      {canRequestToJoin && !joinRequest && !dismissedCallouts.has('requestToJoin') && (
        <Section>
          <TeCallout
            status="info"
            icon="info"
            heading={t('requestToJoinCalloutHeading', { ns: 'common' })}
            text={t('requestToJoinCalloutDescription', { ns: 'common' })}
            grow
            style={{ borderColor: '#005FAD' }}
            closeable
            teclose={() => handleCalloutDismiss('requestToJoin')}
          />
        </Section>
      )}

      <CustomerMigrationCallout />

      <Section>
        <Carousel paths={['banner']} />
      </Section>

      {featureFlags.MIGRATION && featureFlags.MIGRATION_DATA_REVIEW && (
        <Section>
          <MigrationCallout
            descriptionText={t('migrationCallout.overviewDescription')}
            buttonLabel={t('migrationCallout.reviewOrgButton')}
            onButtonClicked={() => navigate(AppRoutes.SettingsGeneralInformation)}
          />
        </Section>
      )}

      {/* DIVISIONS */}
      {showDivisions && (
        <Section>
          <TeContainer alignItems="center" justifyContent="space-between" fill gap="xs">
            <TeContainerSlim gap="xs2" alignItems="center">
              <TeText size={16} weight={700}>
                {t('divisions')}
              </TeText>
              {combinedDivisions && combinedDivisions.length > 0 && (
                <TeText color="text.secondary">({combinedDivisions.length})</TeText>
              )}
            </TeContainerSlim>
            <TeButtonMinimal
              text={t('viewDivisions')}
              size="s"
              postfixIcon="arrow_forward"
              teclick={() => navigate(AppRoutes.Divisions)}
              virtualHref={AppRoutes.Divisions}
            />
          </TeContainer>

          <TeDivider dividerColor="border.secondary" />

          <TeContainer fill style={{ display: 'block' }}>
            {combinedDivisions.length === 0 ? (
              <PlaceholderContainer
                grow
                icon="business"
                text={t('noDivisions')}
                backgroundColor="background.secondary"
              />
            ) : (
              <TeGrid gap="s" padding={0}>
                <RenderDivisions isOverview divisions={combinedDivisions} userId={identity.id} />
              </TeGrid>
            )}
          </TeContainer>
        </Section>
      )}

      {/* USERS KPIs */}
      <Section $direction="row" $gap="xs4" $background={background.tertiary}>
        {isUserAdmin && (
          <UsersKPI
            users={users?.items}
            invitations={invitations}
            requests={requests}
            showReviewUsersCallout={!!hasMigrationCandidatesUsers}
            handleCloseCallout={() => handleCalloutDismiss('users')}
            // Navigates to the Users page with a one-shot deep-link query param;
            // SuggestedUsersModal opens there (see Users.tsx), not here — see ADR 0005.
            onReviewSuggestedUsers={() => navigate(`${AppRoutes.Users}?suggestedUsers=open`)}
          />
        )}
      </Section>

      {/* customers KPI */}
      <Section>
        {currentBusinessPartner?.type === BusinessPartnerType.Dealer && isCustomerAdmin && (
          <CustomersKPI />
        )}
      </Section>

      {/* APPS */}
      <Section>
        <TeContainer alignItems="center" justifyContent="space-between" fill gap="xs">
          <TeContainerSlim gap="xs2" alignItems="center">
            <TeText size={16} weight={700}>
              {t('apps')}
            </TeText>
            {apps && visibleApps?.length > 0 && (
              <TeText color="text.secondary">({visibleApps.length})</TeText>
            )}
          </TeContainerSlim>
          <TeButtonMinimal
            text={t('viewApps')}
            size="s"
            postfixIcon="arrow_forward"
            teclick={() => navigate(AppRoutes.Apps)}
            virtualHref={AppRoutes.Apps}
          />
        </TeContainer>

        <TeDivider dividerColor="border.secondary" />

        <TeContainer column gap="s" fill>
          {visibleApps && visibleApps?.length > 0 ? (
            <RenderApps apps={visibleApps} />
          ) : (
            <EmptyApps />
          )}
        </TeContainer>
      </Section>

      {/* <Section style={{ background: 'transparent' }}>
        <ContentBlocks />
      </Section> */}
    </TeContainer>
  );
}

export default GenericOverview;
